import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import CTABand from "../components/CTABand";
import {
  Footprints, HeartHandshake, Users, HandHeart, ArrowRight, Phone, ChevronDown, ChevronUp,
} from "lucide-react";
import { useState } from "react";
import { AUDIENCE, FAQS, SITE } from "../data/mock";
import { RESOURCES } from "../data/pages";
import { PHOTOS } from "../data/photos";

const ICONS = { Footprints, HeartHandshake, Users, HandHeart };

const NeedHelpPage = () => {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Need Help?"
          title="You don’t have to do this alone."
          description="Whether you’re seeking recovery, sustaining your journey, or supporting a loved one — our peer specialists are here, ready to listen."
          breadcrumbs={[{ label: "Need Help" }]}
          image={PHOTOS.therapy3}
        />

        {/* Helpline strip */}
        <section className="py-8 bg-[hsl(var(--rc-deep))] text-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[hsl(var(--rc-gold))]" />
              </div>
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[hsl(var(--rc-gold))] font-semibold">
                  Helpline
                </div>
                <div className="font-serif-display text-[22px] font-semibold">
                  {SITE.helpline} ({SITE.helplineDigits})
                </div>
              </div>
            </div>
            <a
              href={`tel:${SITE.helplineTel}`}
              className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[hsl(var(--rc-gold))] hover:bg-[hsl(var(--rc-gold))]/90 text-[hsl(var(--rc-deep))] font-semibold text-[14px] transition-colors"
            >
              Call Now <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>

        {/* Audience sections */}
        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl mb-12">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Where you are matters
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                I am a person who is…
              </h2>
            </div>
            <div className="space-y-5">
              {AUDIENCE.map((a, i) => {
                const Icon = ICONS[a.icon] || Footprints;
                return (
                  <div
                    key={a.title}
                    id={a.anchor}
                    className="grid md:grid-cols-12 gap-6 items-center bg-[hsl(var(--rc-paper))] border border-[hsl(var(--border))] rounded-2xl p-7 md:p-9"
                  >
                    <div className="md:col-span-1">
                      <div className="font-serif-display text-[44px] leading-none text-[hsl(var(--rc-purple))]/30 font-semibold">
                        0{i + 1}
                      </div>
                    </div>
                    <div className="md:col-span-1">
                      <div className="w-12 h-12 rounded-xl bg-[hsl(var(--rc-deep))] text-white flex items-center justify-center">
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>
                    <div className="md:col-span-7">
                      <h3 className="font-serif-display text-[24px] md:text-[28px] font-semibold text-[hsl(var(--rc-deep))]">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-[1.7] text-[hsl(var(--rc-slate))]">
                        {a.description}
                      </p>
                    </div>
                    <div className="md:col-span-3 flex md:justify-end">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-2 px-5 h-11 rounded-full bg-[hsl(var(--rc-deep))] text-white text-[14px] font-medium hover:bg-[hsl(var(--rc-purple))] transition-colors"
                      >
                        {a.cta} <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Resources grid */}
        <section className="py-20 md:py-24 rc-section-soft">
          <div className="max-w-7xl mx-auto px-5 md:px-8">
            <div className="max-w-2xl mb-12">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Resources
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Practical paths to support.
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {RESOURCES.map((r) => (
                <a
                  key={r.title}
                  href={r.href}
                  className="rc-card-hover group bg-white border border-[hsl(var(--border))] rounded-2xl p-6 flex flex-col"
                >
                  <h3 className="font-serif-display text-[20px] font-semibold text-[hsl(var(--rc-deep))]">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-[14px] leading-[1.6] text-[hsl(var(--rc-slate))] flex-1">
                    {r.body}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[13px] font-semibold text-[hsl(var(--rc-purple))] group-hover:text-[hsl(var(--rc-deep))]">
                    {r.linkLabel}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 md:py-24 bg-white">
          <div className="max-w-3xl mx-auto px-5 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[12px] tracking-[0.22em] uppercase text-[hsl(var(--rc-purple))] font-semibold">
                Common Questions
              </p>
              <h2 className="font-serif-display mt-3 text-[34px] md:text-[42px] leading-[1.1] font-semibold text-[hsl(var(--rc-deep))]">
                Frequently asked.
              </h2>
            </div>
            <div className="space-y-3">
              {FAQS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div
                    key={f.q}
                    className={`border rounded-xl transition-all ${
                      open
                        ? "border-[hsl(var(--rc-purple))] bg-[hsl(var(--rc-purple-soft))]/30"
                        : "border-[hsl(var(--border))] bg-white"
                    }`}
                  >
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-center justify-between gap-4 p-5 text-left"
                    >
                      <span className="font-serif-display text-[17px] font-semibold text-[hsl(var(--rc-deep))]">
                        {f.q}
                      </span>
                      {open ? (
                        <ChevronUp className="w-4 h-4 text-[hsl(var(--rc-purple))] shrink-0" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                      )}
                    </button>
                    {open && (
                      <div className="px-5 pb-5 text-[14.5px] leading-[1.7] text-[hsl(var(--rc-slate))]">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <CTABand />
      </main>
      <Footer />
    </div>
  );
};

export default NeedHelpPage;
